import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { maasApi } from '@/api/maas.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MaasModelSelect from '@/components/MaasModelSelect';
import { Skeleton } from '@/components/ui/skeleton';
import { SlideOver } from '@/components/ui/slide-over';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoaderIcon, CopyIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons';
import {
  CreateMaasCredentialRequestSchema,
  UpdateMaasCredentialRequestSchema,
} from '@/proto/agentplatform/httpv2/maas_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { MaasModelSchema, type MaasModel } from '@/proto/agentplatform/v2/maas_pb';

function formatTimestamp(ts: number | bigint | undefined) {
  if (!ts || ts === 0n) return '-';
  const date = new Date(Number(ts) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export default function ModelsPage() {
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [formError, setFormError] = useState('');
  const [modelOptions, setModelOptions] = useState<MaasModel[]>([]);
  const [modelsHint, setModelsHint] = useState('');
  /** 避免快速重复触发导致竞态覆盖结果 */
  const modelsFetchGen = useRef(0);

  const loadDefaultModelOptions = useCallback(async () => {
    const gen = ++modelsFetchGen.current;
    setModelsHint('');
    try {
      const trimmedKey = apiKey.trim();
      let models: MaasModel[] = [];

      if (editingId && trimmedKey === '') {
        const resp = await maasApi.listModelsByCredential(editingId);
        models = resp.items ?? [];
      } else {
        if (!trimmedKey) {
          if (gen === modelsFetchGen.current) {
            setModelOptions([]);
            setModelsHint('请先填写 API Key');
          }
          return;
        }
        const resp = await maasApi.listAvailableModels(trimmedKey);
        models = resp.items ?? [];
      }

      if (gen !== modelsFetchGen.current) {
        return;
      }

      // ensure current defaultModel is included even if API doesn't return it
      if (defaultModel && !models.some((m) => m.name === defaultModel)) {
        models = [create(MaasModelSchema, { name: defaultModel }), ...models];
      }
      setModelOptions(models);
      if (models.length === 0) {
        setModelsHint('未获取到列表，请检查 API Key 或网络');
      }
    } catch (err: unknown) {
      if (gen === modelsFetchGen.current) {
        setModelOptions([]);
        setModelsHint(err instanceof Error ? err.message : '请求失败');
      }
    }
  }, [apiKey, editingId, defaultModel]);

  /** 避免把 loadDefaultModelOptions 放进 effect 依赖：defaultModel 更新会改引用，导致编辑抽屉重复按凭证拉取。 */
  const loadDefaultModelOptionsRef = useRef(loadDefaultModelOptions);
  loadDefaultModelOptionsRef.current = loadDefaultModelOptions;

  /** 编辑且密钥留空：进入抽屉或清空密钥时用服务端已存密钥拉列表（无需等失焦）。 */
  useEffect(() => {
    if (!drawerOpen || !editingId || apiKey.trim() !== '') {
      return;
    }
    void loadDefaultModelOptionsRef.current();
  }, [drawerOpen, editingId, apiKey]);

  const handleApiKeyBlur = useCallback(() => {
    if (editingId !== null && apiKey.trim() === '') {
      return;
    }
    void loadDefaultModelOptionsRef.current();
  }, [editingId, apiKey]);

  const { data, isLoading } = useQuery({
    queryKey: ['maas-credentials', page, pageSize],
    queryFn: () => maasApi.listCredentials(create(ListRequestSchema, { page, pageSize })),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) {
        throw new Error('请填写名称');
      }
      if (editingId) {
        const trimmedKey = apiKey.trim();
        return maasApi.updateCredential(
          create(UpdateMaasCredentialRequestSchema, {
            id: editingId,
            name,
            apiKey: trimmedKey,
            defaultModel,
          })
        );
      }
      const trimmedKey = apiKey.trim();
      if (!trimmedKey) {
        throw new Error('请填写 API Key');
      }
      return maasApi.createCredential(
        create(CreateMaasCredentialRequestSchema, {
          name,
          apiKey: trimmedKey,
          defaultModel,
        })
      );
    },
    onSuccess: () => {
      setDrawerOpen(false);
      setFormError('');
      queryClient.invalidateQueries({ queryKey: ['maas-credentials'] });
    },
    onError: (err: unknown) => {
      setFormError(err instanceof Error ? err.message : '保存失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => maasApi.deleteCredential(id),
    onSuccess: () => {
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['maas-credentials'] });
    },
  });

  const [copyingId, setCopyingId] = useState<string | null>(null);
  const copyKey = useCallback(async (id: string) => {
    setCopyingId(id);
    try {
      const key = await maasApi.copyCredentialKey(id);
      await navigator.clipboard.writeText(key);
      toast.success('已复制到剪贴板');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '复制失败');
    } finally {
      setCopyingId(null);
    }
  }, []);

  const rows = data?.items ?? [];
  const filtered = searchKeyword.trim()
    ? rows.filter((x) => x.name.toLowerCase().includes(searchKeyword.trim().toLowerCase()))
    : rows;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() {
    setEditingId(null);
    setName('');
    setApiKey('');
    setDefaultModel('');
    setFormError('');
    setModelOptions([]);
    setModelsHint('');
    setDrawerOpen(true);
  }

  function openEdit(id: string) {
    const row = rows.find((x) => x.id === id);
    if (!row) return;
    setEditingId(id);
    setName(row.name);
    setApiKey('');
    setDefaultModel(row.defaultModel);
    setFormError('');
    setModelOptions([]);
    setModelsHint('');
    setDrawerOpen(true);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold tracking-tight">模型</h2>
        <div className="flex items-center gap-3">
          <Input
            className="w-56"
            placeholder="按名称搜索..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          <Button onClick={openCreate} className="gap-2">
            <PlusIcon className="h-4 w-4" />
            新增模型凭证
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="border rounded-lg p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>名称</TableHead>
                <TableHead>默认模型</TableHead>
                <TableHead>API Key</TableHead>
                <TableHead>更新时间</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                    暂无模型凭证
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.defaultModel || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{item.apiKey || '-'}</TableCell>
                    <TableCell>{formatTimestamp(item.updatedAt)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => copyKey(item.id)}
                          disabled={copyingId === item.id}
                          title="复制 API Key"
                        >
                          {copyingId === item.id ? (
                            <LoaderIcon className="h-4 w-4" />
                          ) : (
                            <CopyIcon className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(item.id)}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(item.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow>
                <TableCell colSpan={5} className="bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">共 {total} 条</span>
                    <div className="flex items-center gap-2">
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        {page}/{totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      <SlideOver
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={editingId ? '编辑模型凭证' : '新增模型凭证'}
        actions={
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="gap-2"
          >
            {saveMutation.isPending && <LoaderIcon className="h-4 w-4" />}保存
          </Button>
        }
      >
        <div className="space-y-4">
          {!!formError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {formError}
            </div>
          )}
          {!editingId && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              请使用 Infini AI{' '}
              <a
                href="https://cloud.infini-ai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                https://cloud.infini-ai.com/
              </a>{' '}
              MaaS 服务
            </p>
          )}
          <div className="space-y-2">
            <Label>名称</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="必填"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{editingId ? 'API Key（留空表示不更新）' : 'API Key'}</Label>
            <Input
              value={apiKey}
              onChange={(e) => {
                const v = e.target.value;
                setApiKey(v);
                if (!editingId && v.trim() === '') {
                  setModelOptions([]);
                  setModelsHint('');
                }
              }}
              onBlur={handleApiKeyBlur}
              placeholder={editingId ? undefined : '必填'}
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label>默认模型</Label>
            <MaasModelSelect
              models={modelOptions}
              value={defaultModel}
              onChange={setDefaultModel}
              placeholder="请选择默认模型"
              triggerClassName="h-9"
            />
            {!!modelsHint && <p className="text-xs text-muted-foreground">{modelsHint}</p>}
          </div>
        </div>
      </SlideOver>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除模型凭证？</AlertDialogTitle>
            <AlertDialogDescription>删除后无法恢复。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteId(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="h-4 w-4 mr-2" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
